// Lightweight modal showing details for an unknown_mentions handle BEFORE
// onboarding (i.e. before a social_account exists for it). Used by the
// Recommended Creators tab so admin can review profile + recent captions
// before clicking Add.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCloseOnTabSwitch } from '@shared/hooks/useCloseOnTabSwitch';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton } from '@shared/ui';
import { SignedImg } from '@shared/ui/SignedMedia';
import PostViewerModal, { type PostViewerData } from '@shared/ui/PostViewerModal';

interface RawPost {
  caption?: string | null;
  timestamp?: string | null;
  type?: string | null;             // 'Image' | 'Video' | 'Sidecar' (carousel)
  likesCount?: number | null;
  commentsCount?: number | null;
  shortCode?: string | null;
  displayUrl?: string | null;       // ephemeral IG CDN URL
  mirrored_url?: string | null;     // permanent Supabase Storage URL (set by mirror_candidate_thumbs.py)
  childPosts?: Array<{ mirrored_url?: string | null; displayUrl?: string | null; url?: string | null }> | null; // carousel slides
  hashtags?: string[] | null;
  mentions?: string[] | null;
  isPinned?: boolean | null;
}

interface UnknownMentionRow {
  handle: string;
  full_name: string | null;
  bio: string | null;
  ig_category: string | null;
  follower_count: number | null;
  following_count: number | null;
  posts_count: number | null;
  is_business: boolean | null;
  is_verified: boolean | null;
  is_private: boolean | null;
  profile_pic_url: string | null;
  profile_pic_mirrored_url: string | null;
  external_url: string | null;
  enriched_at: string | null;
  latest_posts_thumbs_mirrored_at: string | null;
  highlight_reel_count: number | null;
  igtv_video_count: number | null;
  joined_recently: boolean | null;
  classify_kind: string | null;
  classify_confidence: number | null;
  classify_reasoning: string | null;
  classify_niche: string | null;
  classify_niche_ids: string[] | null;
  classify_display_name: string | null;
  classify_subcategory_id: string | null;
  classify_subcategory_conf: number | null;
  classify_brand_id: string | null;
  classify_brand_conf: number | null;
  classify_model: string | null;
  classify_ran_at: string | null;
  mention_count: number | null;
  post_count: number | null;
  story_count: number | null;
  distinct_bloggers: number | null;
  latest_posts_raw: RawPost[] | null;
}

export default function SuggestedHandleCard({
  handle, onClose,
}: { handle: string; onClose: () => void }) {
  useCloseOnTabSwitch(onClose, true);
  // Post clicked → opened in the shared lightbox (mirrored thumb). When the
  // lightbox is open it owns Escape, so don't also close the whole card.
  const [viewPost, setViewPost] = useState<RawPost | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !viewPost) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, viewPost]);

  // Lookup table: niche_id → name_ru. Small table (77 rows), cached by react-query.
  const { data: nicheRows } = useSupabaseQuery<{ id: string; name_ru: string }[]>(
    ['niches-lookup'],
    async () => supabase.from('niches').select('id, name_ru').order('name_ru'),
  );
  const nicheById = new Map((nicheRows ?? []).map((n) => [n.id, n.name_ru]));

  const { data, isLoading, error } = useSupabaseQuery<UnknownMentionRow>(
    ['suggested-handle-card', handle],
    async () =>
      supabase
        .from('unknown_mentions')
        .select('handle, full_name, bio, ig_category, follower_count, following_count, posts_count, is_business, is_verified, is_private, profile_pic_url, profile_pic_mirrored_url, external_url, enriched_at, classify_kind, classify_confidence, classify_reasoning, classify_niche, classify_niche_ids, classify_display_name, classify_subcategory_id, classify_subcategory_conf, classify_brand_id, classify_brand_conf, classify_model, classify_ran_at, mention_count, post_count, story_count, distinct_bloggers, latest_posts_raw, latest_posts_thumbs_mirrored_at, highlight_reel_count, igtv_video_count, joined_recently')
        .eq('handle', handle)
        .maybeSingle(),
  );

  const posts = (data?.latest_posts_raw ?? [])
    .slice()
    .sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''))
    .slice(0, 12);

  // Aggregate hashtags + mentions across the 12 posts (frequency)
  const hashCounts = new Map<string, number>();
  const mentionCounts = new Map<string, number>();
  for (const p of posts) {
    for (const h of (p.hashtags ?? [])) hashCounts.set(h, (hashCounts.get(h) ?? 0) + 1);
    for (const m of (p.mentions ?? [])) mentionCounts.set(m, (mentionCounts.get(m) ?? 0) + 1);
  }
  const topHashtags = Array.from(hashCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12);
  const topMentions = Array.from(mentionCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12);

  return createPortal(
    <div
      role="dialog" aria-modal="true" onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '6vh 16px', backdropFilter: 'blur(4px)', overflow: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560, background: 'var(--bg-elev, #161b22)',
          border: '1px solid rgba(15,45,100,0.5)', borderRadius: 12, padding: 20,
          color: '#dde',
        }}
      >
        {isLoading && <Skeleton height={300} />}
        {error && <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>}
        {data && (
          <>
            {/* Header */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
              <ProfilePic url={data.profile_pic_mirrored_url || data.profile_pic_url} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: 'var(--fs-h2)' }}>{data.full_name || data.handle}</h3>
                  {data.is_verified && (
                    <span title="Verified on Instagram" style={{ color: '#4a9eff', fontSize: 'var(--fs-body)' }}>✓</span>
                  )}
                </div>
                <a
                  href={`https://www.instagram.com/${data.handle}/`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 'var(--fs-body)', color: '#9ec3ff', textDecoration: 'none' }}
                >@{data.handle} ↗</a>
                <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {data.follower_count != null && <span><strong>{data.follower_count.toLocaleString()}</strong> followers</span>}
                  {data.following_count != null && <span>· <strong>{data.following_count.toLocaleString()}</strong> following</span>}
                  {data.posts_count != null && <span>· {data.posts_count.toLocaleString()} posts</span>}
                  {data.ig_category
                    ? <span>· {data.ig_category}</span>
                    : <span style={{ color: '#556' }}>· (no IG category)</span>}
                </div>
                <div style={{ fontSize: 'var(--fs-caption)', color: '#778', marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {data.is_business && <span style={chip()}>business</span>}
                  {data.is_private && <span style={chip('#e74c3c')}>private</span>}
                  {data.joined_recently && <span style={chip('#f5b700')}>new account</span>}
                  {data.highlight_reel_count != null && data.highlight_reel_count > 0 && (
                    <span style={chip()}>{data.highlight_reel_count} highlights</span>
                  )}
                  {data.igtv_video_count != null && data.igtv_video_count > 0 && (
                    <span style={chip()}>{data.igtv_video_count} IGTV</span>
                  )}
                  {data.classify_kind && (
                    <span style={chip(data.classify_kind === 'brand' ? '#4a9eff' : '#2ecc71')}>
                      🤖 {data.classify_kind} · {Math.round((data.classify_confidence ?? 0) * 100)}%
                    </span>
                  )}
                  {/* AI-suggested niches (resolved from niche_ids → name_ru) */}
                  {data.classify_niche_ids?.map((nid) => {
                    const name = nicheById.get(nid);
                    if (!name) return null;
                    return (
                      <span
                        key={nid}
                        title="AI niche suggestion (will be copied to instagram_providers.niche_ids on Add)"
                        style={chip('#b18cf0')}
                      >🎯 {name}</span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ ...box, marginBottom: 10 }}>
              <div style={label}>Bio</div>
              {data.bio
                ? <div style={{ fontSize: 'var(--fs-body)', whiteSpace: 'pre-wrap' }}>{data.bio}</div>
                : <div style={{ fontSize: 'var(--fs-small)', color: '#556', fontStyle: 'italic' }}>(no bio captured)</div>}
            </div>

            <div style={{ ...box, marginBottom: 10 }}>
              <div style={label}>External URL</div>
              {data.external_url
                ? <a href={data.external_url} target="_blank" rel="noopener noreferrer"
                     style={{ fontSize: 'var(--fs-small)', color: '#9ec3ff', wordBreak: 'break-all' }}>
                    {data.external_url}
                  </a>
                : <div style={{ fontSize: 'var(--fs-small)', color: '#556', fontStyle: 'italic' }}>(no external URL captured)</div>}
            </div>

            <div style={{ ...box, marginBottom: 10 }}>
              <div style={label}>Mention activity in our scrape</div>
              <div style={{ fontSize: 'var(--fs-small)', color: '#bcd0ff' }}>
                {data.mention_count ?? 0} total appearances · {data.post_count ?? 0} posts ·
                {' '}{data.story_count ?? 0} stories · {data.distinct_bloggers ?? 0} distinct creators
              </div>
            </div>

            {/* 12-post grid: thumbnails (mirrored when available) + caption + likes/comments */}
            <div style={{ ...box, marginBottom: 10 }}>
              <div style={label}>
                Recent posts ({posts.length} captured of up to 12)
                {data.latest_posts_thumbs_mirrored_at
                  ? <span style={{ color: '#2ecc71', marginLeft: 8 }}>· thumbnails mirrored</span>
                  : posts.length > 0 && <span style={{ color: '#f5b700', marginLeft: 8 }}>· thumbnails not mirrored (may fail to load)</span>}
              </div>
              {posts.length === 0
                ? <div style={{ fontSize: 'var(--fs-small)', color: '#556', fontStyle: 'italic' }}>(no posts captured — Apify returned profile only)</div>
                : <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 8,
                  }}>
                    {posts.map((p, i) => (
                      <PostTile key={p.shortCode ?? i} post={p} onOpen={() => setViewPost(p)} />
                    ))}
                  </div>}
            </div>

            {(topHashtags.length > 0 || topMentions.length > 0) && (
              <div style={{ ...box, marginBottom: 10 }}>
                <div style={label}>From posts</div>
                {topHashtags.length > 0 && (
                  <div style={{ fontSize: 'var(--fs-caption)', color: '#9aa', marginBottom: 4 }}>
                    <strong style={{ color: '#bcd0ff' }}>Top hashtags:</strong>{' '}
                    {topHashtags.map(([h, n]) => (
                      <span key={h} style={{ marginRight: 6 }}>#{h}{n > 1 && <sub>×{n}</sub>}</span>
                    ))}
                  </div>
                )}
                {topMentions.length > 0 && (
                  <div style={{ fontSize: 'var(--fs-caption)', color: '#9aa' }}>
                    <strong style={{ color: '#bcd0ff' }}>Frequently tags:</strong>{' '}
                    {topMentions.map(([m, n]) => (
                      <span key={m} style={{ marginRight: 6 }}>@{m}{n > 1 && <sub>×{n}</sub>}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {data.classify_reasoning && (
              <div style={{ ...box, marginBottom: 10 }}>
                <div style={label}>Classification reasoning ({data.classify_model})</div>
                <div style={{ fontSize: 'var(--fs-caption)', color: '#bdd', fontStyle: 'italic' }}>
                  {data.classify_reasoning}
                </div>
              </div>
            )}

            <div style={{ textAlign: 'right', marginTop: 12 }}>
              <button
                type="button" onClick={onClose}
                style={{
                  padding: '6px 14px', background: 'transparent', color: '#9aa',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-body)',
                }}
              >Close</button>
            </div>

            {viewPost && (
              <PostViewerModal
                post={toViewerData(viewPost, data.handle)}
                onClose={() => setViewPost(null)}
              />
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Map a raw Apify post into the shared lightbox's shape (mirrored thumb first). */
function toViewerData(p: RawPost, handle: string): PostViewerData {
  const isCarousel = p.type === 'Sidecar';
  return {
    id: p.shortCode ?? undefined,
    post_type: p.type === 'Video' ? 'video' : isCarousel ? 'carousel' : 'image',
    thumb_url: p.mirrored_url || p.displayUrl || null,
    // Pass carousel slides so PostViewerModal can scroll them. Map to {slides:[{public_url}]}
    // (the shape extractCarouselSlides reads first), preferring the permanent mirrored URL.
    child_posts: isCarousel && Array.isArray(p.childPosts)
      ? { slides: p.childPosts.map((c) => ({ public_url: c.mirrored_url || c.displayUrl || c.url || null })) }
      : undefined,
    caption: p.caption ?? null,
    likes: p.likesCount ?? null,
    comments: p.commentsCount ?? null,
    posted_at: p.timestamp ?? null,
    permalink: p.shortCode ? `https://www.instagram.com/p/${p.shortCode}/` : null,
    handle,
  };
}

function ProfilePic({ url }: { url: string | null }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) {
    return (
      <div style={{
        width: 72, height: 72, borderRadius: '50%',
        background: 'rgba(15,45,100,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 'var(--fs-h1)', color: '#778',
        border: '1px solid rgba(15,45,100,0.5)',
        flex: '0 0 auto',
      }}>?</div>
    );
  }
  return (
    <SignedImg
      src={url} alt=""
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
      style={{
        width: 72, height: 72, borderRadius: '50%', objectFit: 'cover',
        border: '1px solid rgba(15,45,100,0.5)',
        flex: '0 0 auto',
      }}
    />
  );
}

function PostTile({ post, onOpen }: { post: RawPost; onOpen: () => void }) {
  const [thumbBroken, setThumbBroken] = useState(false);
  const thumbUrl = post.mirrored_url || post.displayUrl || null;
  const showThumb = thumbUrl && !thumbBroken;
  const igLink = post.shortCode ? `https://www.instagram.com/p/${post.shortCode}/` : null;
  const typeBadge =
    post.type === 'Video'   ? '▶'    :
    post.type === 'Sidecar' ? '🖼️🖼️' :
    null;

  return (
    <div
      role="button" tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      title={(post.caption ?? '').slice(0, 400)}
      style={{
        display: 'block', position: 'relative',
        aspectRatio: '1 / 1', borderRadius: 6, overflow: 'hidden',
        border: '1px solid rgba(15,45,100,0.4)',
        background: 'rgba(0,0,0,0.4)',
        cursor: 'pointer',
      }}
    >
      {showThumb ? (
        <SignedImg
          src={thumbUrl ?? ''} alt=""
          referrerPolicy="no-referrer"
          onError={() => setThumbBroken(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div style={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--fs-micro)', color: '#778', textAlign: 'center', padding: 6,
        }}>
          {thumbUrl ? 'thumb expired' : 'no thumb'}
        </div>
      )}
      {/* Type + pinned badges */}
      {(typeBadge || post.isPinned) && (
        <div style={{
          position: 'absolute', top: 4, right: 4,
          background: 'rgba(0,0,0,0.6)', borderRadius: 4,
          padding: '1px 4px', fontSize: 'var(--fs-micro)', color: '#fff',
        }}>
          {post.isPinned && '📌'}{typeBadge}
        </div>
      )}
      {/* Small separate link to the original Instagram post */}
      {igLink && (
        <a
          href={igLink}
          target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open on Instagram"
          aria-label="Open on Instagram"
          style={{
            position: 'absolute', top: 4, left: 4,
            background: 'rgba(0,0,0,0.6)', borderRadius: 4,
            padding: '1px 5px', fontSize: 'var(--fs-micro)', lineHeight: '14px',
            color: '#bcd0ff', textDecoration: 'none',
          }}
        >↗</a>
      )}
      {/* Likes / comments overlay */}
      {(post.likesCount != null || post.commentsCount != null) && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(0,0,0,0.7)', color: '#bcd0ff',
          fontSize: 'var(--fs-micro)', padding: '2px 4px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {post.likesCount != null && `♥ ${post.likesCount.toLocaleString()}`}
          {post.likesCount != null && post.commentsCount != null && ' · '}
          {post.commentsCount != null && `💬 ${post.commentsCount.toLocaleString()}`}
        </div>
      )}
    </div>
  );
}

function chip(color = '#9B5DE5'): React.CSSProperties {
  return {
    padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-semibold)',
    color, border: `1px solid ${color}`,
    background: 'rgba(0,0,0,0.2)',
  };
}
const box: React.CSSProperties = {
  background: 'rgba(0,0,0,0.25)',
  border: '1px solid rgba(15,45,100,0.4)',
  borderRadius: 8, padding: '8px 10px',
};
const label: React.CSSProperties = {
  fontSize: 'var(--fs-micro)', color: '#9aa', textTransform: 'uppercase',
  letterSpacing: 0.5, marginBottom: 4,
};
